// Revolt Table Component
// Unified table component for consistent data display
// Supports headers, rows, row click handlers, custom column styling, and optional sorting

import './RevoltTable.css';
import { RevoltIcon } from './RevoltIcon';

export function RevoltTable({
  headers = [],
  rows = [],
  gridColumns = null, // Column widths: string like "1fr 2fr auto", array like [1, 2, 'auto'], or null for equal columns
  onRowClick = null,
  emptyMessage = 'No data available.',
  className = '',
  // Sorting props (optional)
  enableSorting = false,     // Enable sorting UI for specified columns
  sortableColumns = [],     // Array of column indices or header names that can be sorted
  sortState = {},           // Current sort state: { columnIndex: 'asc'|'desc'|null }
  onSortChange = null,      // Callback: (columnIndex, newDirection) => void
  ...props
}) {
  // Convert gridColumns to CSS string
  const getGridColumns = () => {
    if (!gridColumns) {
      return `repeat(${headers.length}, 1fr)`;
    }

    if (typeof gridColumns === 'string') {
      return gridColumns;
    }

    if (Array.isArray(gridColumns)) {
      return gridColumns.map(col => {
        if (typeof col === 'number') {
          return `${col}fr`;
        }
        return col; // Assume it's already a valid CSS value like 'auto', '100px', etc.
      }).join(' ');
    }

    return `repeat(${headers.length}, 1fr)`;
  };

  const defaultGridColumns = getGridColumns();

  // Handle sort button clicks - cycle through: null -> 'desc' -> 'asc' -> null
  const handleSortClick = (columnIndex) => {
    if (!onSortChange) return;

    const currentState = sortState[columnIndex] || null;
    const nextState = {
      null: 'desc',    // neutral -> descending
      'desc': 'asc',   // descending -> ascending
      'asc': null      // ascending -> neutral
    }[currentState];

    onSortChange(columnIndex, nextState);
  };

  // Check if a column is sortable
  const isColumnSortable = (index, header) => {
    if (!enableSorting) return false;
    return sortableColumns.includes(index) || sortableColumns.includes(header);
  };

  const tableClasses = [
    'revolt-table',
    className
  ].filter(Boolean).join(' ');

  const headerClasses = [
    'revolt-table-header',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={tableClasses} {...props}>
      {/* Table Header */}
      {headers.length > 0 && (
        <div
          className={headerClasses}
          style={{ gridTemplateColumns: defaultGridColumns }}
        >
          {headers.map((header, index) => {
            const isSortable = isColumnSortable(index, header);
            const currentSortState = sortState[index] || null;

            return (
              <span key={index} className={`revolt-table-header-cell ${isSortable ? 'sortable' : ''}`}>
                {isSortable ? (
                  <button
                    className="revolt-table-header-btn"
                    onClick={() => handleSortClick(index)}
                    title={`Sort by ${header}`}
                  >
                    {header}
                    <span className="sort-icon">
                      {currentSortState === 'asc' && (
                        <RevoltIcon name="arrow-up" size="sm" color="#ff4444" />
                      )}
                      {currentSortState === 'desc' && (
                        <RevoltIcon name="arrow-down" size="sm" color="#ff4444" />
                      )}
                      {currentSortState === null && (
                        <RevoltIcon name="minus" size="sm" color="#ff4444" />
                      )}
                    </span>
                  </button>
                ) : (
                  header
                )}
              </span>
            );
          })}
        </div>
      )}

      {/* Table Rows */}
      <div className="revolt-table-body">
        {rows.length === 0 ? (
          <div className="revolt-table-empty">
            {emptyMessage}
          </div>
        ) : (
          rows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={`revolt-table-row ${onRowClick ? 'clickable' : ''}`}
              style={{ gridTemplateColumns: defaultGridColumns }}
              onClick={() => onRowClick && onRowClick(row, rowIndex)}
            >
              {Array.isArray(row) ? (
                row.map((cell, cellIndex) => (
                  <span key={cellIndex} className={`revolt-table-cell cell-${cellIndex}`}>
                    {cell}
                  </span>
                ))
              ) : (
                // Support for object rows - render each property as a cell
                Object.values(row).map((value, cellIndex) => (
                  <span key={cellIndex} className={`revolt-table-cell cell-${cellIndex}`}>
                    {value}
                  </span>
                ))
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Helper component for table cells with custom content (buttons, etc.)
export function RevoltTableCell({ children, className = '', ...props }) {
  const cellClasses = [
    'revolt-table-cell',
    className
  ].filter(Boolean).join(' ');

  return (
    <span className={cellClasses} {...props}>
      {children}
    </span>
  );
}
